import { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Theme provider, following the shadcn Vite dark-mode recipe, with three
 * changes this app needs:
 *
 *   1. **The storage key is `dove.theme`, shared with the pre-paint bootstrap
 *      in `index.html`.** shadcn's version resolves the theme in an effect,
 *      which runs after first paint, so the page renders light and then flips,
 *      and on a phone that flash is the first thing a user sees. The bootstrap
 *      already sets the class before paint; this provider adopts its key rather
 *      than introducing a second source of truth.
 *   2. **`system` keeps tracking the OS.** The recipe reads
 *      `prefers-color-scheme` once per theme change, so a machine switching to
 *      dark at sunset does not follow until reload. This subscribes.
 *   3. **`resolvedTheme` is exposed**, because `system` is not a colour and
 *      anything that has to *toggle*, the keyboard shortcut, the status-bar
 *      colour, needs to know which way it currently resolves.
 */
export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'dove.theme';

type ThemeProviderState = {
  theme: Theme;
  /** What `theme` actually evaluates to right now. Never `system`. */
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  /** Flip between light and dark from wherever the theme currently resolves. */
  toggleTheme: () => void;
};

const ThemeProviderContext = createContext<ThemeProviderState | undefined>(
  undefined,
);

function prefersDark() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readStored(): Theme | null {
  try {
    /* `?theme=` is a dev affordance for checking a scheme without touching OS
       settings; the bootstrap honours the same parameter. */
    const q = new URLSearchParams(location.search).get('theme');
    const raw = q || localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    /* Private-mode localStorage throws. */
  }
  return null;
}

export function ThemeProvider({
  children,
  defaultTheme = 'system',
}: {
  children: React.ReactNode;
  defaultTheme?: Theme;
}) {
  const [theme, setThemeState] = useState<Theme>(
    () => readStored() ?? defaultTheme,
  );
  const [systemDark, setSystemDark] = useState(prefersDark);

  /* Follow the OS for as long as the user has not overridden it. */
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', resolvedTheme === 'dark');
    root.style.colorScheme = resolvedTheme;

    /* Keeps the Capacitor status bar and the PWA chrome in step with a manual
       override, a media-query-only `theme-color` cannot follow one. */
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', resolvedTheme === 'dark' ? '#0a0a0a' : '#ffffff');
  }, [resolvedTheme]);

  const value = useMemo<ThemeProviderState>(() => {
    const setTheme = (next: Theme) => {
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* Ignore: the theme still applies for this session. */
      }
      setThemeState(next);
    };
    return {
      theme,
      resolvedTheme,
      setTheme,
      /* Toggling from `system` picks the opposite of what you can currently
         see, which is the only thing that isn't a no-op. */
      toggleTheme: () => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark'),
    };
  }, [theme, resolvedTheme]);

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeProviderContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
}
